import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Plus,
  Calendar,
  Users,
  Banknote,
  Grid3X3,
  List,
  BarChart3,
  Eye,
  Edit,
  MoreVertical,
  Settings,
  Archive
} from "lucide-react";
import { projectService } from "@/services/projectService";
import { useAuth } from "@/contexts/AuthContext";
import { selectApiData } from "@/services/api";
import { formatCurrency } from "@/utils/formatters";
import toast from 'react-hot-toast';

const Projects = () => {
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [filters, setFilters] = useState({
    search: "",
    status: [],
    priority: [],
    manager_id: [],
    department_id: [],
    budget_range: "",
    progress_range: "",
    start_date_from: "",
    start_date_to: "",
    end_date_from: "",
    end_date_to: "",
    is_billable: "",
    sort: "created_at_desc",
  });
  
  const {
    isAuthenticated,
    user,
    token,
    isLoading: authLoading,
  } = useAuth();

  // Debug auth state
  // console.log('Auth state:', { isAuthenticated, hasUser: !!user, hasToken: !!token, authLoading })

  // If auth is still loading, show loading state
  if (authLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-6">
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
                <div className="h-2 bg-gray-300 rounded w-full mb-4"></div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const queryClient = useQueryClient();

  const {
    data: projects,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["projects", filters],
    () => projectService.getProjects(filters),
    {
      enabled: isAuthenticated && !!token,
      select: selectApiData,
      keepPreviousData: true,
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        console.error('Projects API error:', error);
        toast.error('Failed to load projects');
      },
      onSuccess: (data) => {
        console.log('Projects data received:', data);
      }
    }
  );

  // Calculate progress from deliverables for each project
  const projectsWithProgress = React.useMemo(() => {
    const projectsList = projects?.projects || []
    if (!projectsList.length) return []
    
    return projectsList.map(project => {
      let milestones = project.milestones
      if (typeof milestones === 'string') {
        try { milestones = JSON.parse(milestones) } catch { milestones = [] }
      }
      if (!Array.isArray(milestones)) milestones = []
      
      const allDeliverables = milestones.flatMap(m => m.deliverables || [])
      const totalDeliverables = allDeliverables.length
      const completedDeliverables = allDeliverables.filter(d => d.completed).length
      const calculatedProgress = totalDeliverables > 0 ? Math.round((completedDeliverables / totalDeliverables) * 100) : 0
      
      return {
        ...project,
        progress_percentage: calculatedProgress
      }
    })
  }, [projects])

  const archiveMutation = useMutation(
    (projectId) => projectService.archiveProject(projectId),
    {
      onSuccess: () => {
        toast.success('Project archived successfully');
        queryClient.invalidateQueries(['projects']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to archive project');
      }
    }
  );

  const updateStatusMutation = useMutation(
    ({ id, status }) => projectService.updateProject(id, { status }),
    {
      onSuccess: () => {
        toast.success('Status updated');
        queryClient.invalidateQueries(['projects']);
      },
      onError: () => toast.error('Failed to update status')
    }
  );

  const handleArchive = (projectId) => {
    if (window.confirm('Are you sure you want to archive this project?')) {
      archiveMutation.mutate(projectId);
    }
  };

  // Simple Project Actions Component
  const ProjectActions = ({ project }) => {
    const [showDropdown, setShowDropdown] = useState(false);

    return (
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
        
        {showDropdown && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
            <div className="py-1">
              <Link
                to={`/projects/${project.id}`}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setShowDropdown(false)}
              >
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </Link>
              <Link
                to={`/projects/${project.id}/edit`}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setShowDropdown(false)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Project
              </Link>
              <Link
                to={`/projects/${project.id}/tasks`}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setShowDropdown(false)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Manage Tasks
              </Link>
              <Link
                to={`/projects/${project.id}/team`}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setShowDropdown(false)}
              >
                <Users className="h-4 w-4 mr-2" />
                Manage Team
              </Link>
              <div className="border-t border-gray-100 my-1"></div>
              <button
                onClick={() => {
                  setShowDropdown(false);
                  handleArchive(project.id);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-amber-600 hover:bg-amber-50"
              >
                <Archive className="h-4 w-4 mr-2" />
                Archive Project
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: "bg-gray-100 text-gray-800",
      active: "bg-green-100 text-green-800",
      on_hold: "bg-yellow-100 text-yellow-800",
      completed: "bg-blue-100 text-blue-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "bg-green-100 text-green-800",
      medium: "bg-yellow-100 text-yellow-800",
      high: "bg-orange-100 text-orange-800",
      critical: "bg-red-100 text-red-800",
    };
    return colors[priority] || "bg-gray-100 text-gray-800";
  };

  const displayProjects = projectsWithProgress || [];

  const handleRefresh = () => {
    refetch();
  };

  // Only show error boundary if we don't have any projects data
  if (error && !projects?.projects?.length) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-red-400 mb-4">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Unable to Load Projects
        </h3>
        <p className="text-gray-600 mb-4">
          There was an error connecting to the server.
        </p>
        <p className="text-sm text-gray-500">
          Please check your connection and try again.
        </p>
        <div className="mt-6">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <div className="flex items-center space-x-2">
            <p className="text-gray-600">Manage and track all your projects</p>
            {error && displayProjects.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                Demo Mode
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to="/projects/dashboard"
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </Link>
          <Link
            to="/projects/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Link>
        </div>
      </div>

      {/* View Controls */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">
            {displayProjects.length} project
            {displayProjects.length !== 1 ? "s" : ""} found
          </span>
          {error && (
            <span className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded">
              Showing cached data
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode("grid")}
            className={`p-2 rounded-md ${
              viewMode === "grid"
                ? "bg-blue-100 text-blue-600"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <Grid3X3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded-md ${
              viewMode === "list"
                ? "bg-blue-100 text-blue-600"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={handleRefresh}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            title="Refresh"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Projects Display */}
      <div
        className={
          viewMode === "grid"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "space-y-4"
        }
      >
        {isLoading
          ? // Loading skeleton
            Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow p-6 animate-pulse"
              >
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
                <div className="h-2 bg-gray-300 rounded w-full mb-4"></div>
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                </div>
              </div>
            ))
          : displayProjects.map((project) =>
              viewMode === "grid" ? (
                // Grid View
                <div
                  key={project.id}
                  className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <Link
                          to={`/projects/${project.id}`}
                          className="text-lg font-semibold text-gray-900 hover:text-blue-600"
                        >
                          {project.name}
                        </Link>
                        <p className="text-sm text-gray-500">{project.code}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <select
                          value={project.status}
                          onChange={(e) => updateStatusMutation.mutate({ id: project.id, status: e.target.value })}
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${getStatusColor(project.status)}`}
                        >
                          <option value="planning">Planning</option>
                          <option value="active">Active</option>
                          <option value="on_hold">On Hold</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        <ProjectActions project={project} />
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{project.progress_percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${project.progress_percentage}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Project Info */}
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          <span>
                            Due:{" "}
                            {new Date(project.end_date).toLocaleDateString()}
                          </span>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(
                            project.priority
                          )}`}
                        >
                          {project.priority}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2" />
                          <span>{project.member_count || 0} members</span>
                        </div>
                        <div className="flex items-center">
                          <Banknote className="h-4 w-4 mr-1" />
                          <span>
                            {formatCurrency(project.budget_spent || 0)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-500">Manager:</span>
                        <span className="ml-2">
                          {project.Manager?.User?.first_name ||
                            project.manager?.first_name}{" "}
                          {project.Manager?.User?.last_name ||
                            project.manager?.last_name}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // List View
                <div
                  key={project.id}
                  className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="flex-1">
                          <Link
                            to={`/projects/${project.id}`}
                            className="text-lg font-semibold text-gray-900 hover:text-blue-600"
                          >
                            {project.name}
                          </Link>
                          <p className="text-sm text-gray-500">
                            {project.code}
                          </p>
                        </div>

                        <div className="flex items-center space-x-6">
                          <div className="text-center">
                            <p className="text-sm text-gray-500">Status</p>
                            <select
                              value={project.status}
                              onChange={(e) => updateStatusMutation.mutate({ id: project.id, status: e.target.value })}
                              className={`px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${getStatusColor(project.status)}`}
                            >
                              <option value="planning">Planning</option>
                              <option value="active">Active</option>
                              <option value="on_hold">On Hold</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>

                          <div className="text-center">
                            <p className="text-sm text-gray-500">Priority</p>
                            <span
                              className={`text-sm font-medium ${getPriorityColor(
                                project.priority
                              )}`}
                            >
                              {project.priority}
                            </span>
                          </div>

                          <div className="text-center">
                            <p className="text-sm text-gray-500">Progress</p>
                            <div className="flex items-center space-x-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{
                                    width: `${project.progress_percentage}%`,
                                  }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium">
                                {project.progress_percentage}%
                              </span>
                            </div>
                          </div>

                          <div className="text-center">
                            <p className="text-sm text-gray-500">Due Date</p>
                            <p className="text-sm font-medium">
                              {new Date(project.end_date).toLocaleDateString()}
                            </p>
                          </div>

                          <div className="text-center">
                            <p className="text-sm text-gray-500">Budget</p>
                            <p className="text-sm font-medium">
                              {formatCurrency(project.budget_spent || 0)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <ProjectActions project={project} />
                    </div>
                  </div>
                </div>
              )
            )}
      </div>

      {/* Empty State */}
      {!isLoading && displayProjects.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No projects found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new project.
          </p>
          <div className="mt-6">
            <Link
              to="/projects/new"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Project
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
